import { useState, useEffect } from 'react';

import {
    Box,
    Typography,
    Button,
    LinearProgress
} from '@mui/material';

import { useApis } from '../../../../api/useApiClient';
import { useNotification } from '../../../../context/NotificationContext';

import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';

import ItemPicker from '../../../Common/Form/ItemPicker';
import CustomersList from '../../../Customer/CustomersList';

export default function CustomerSelector({ workOrder, disabled = false, onCustomerChange }) {

    const { tenantApi } = useApis();
    const { showNotification } = useNotification();

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    useEffect(() => {
        // Set selected customer from work order data
        setSelectedCustomer(workOrder?.customer || null);
    }, [workOrder]);

    const [loading, setLoading] = useState(false);

    function handleSelectCustomer(selectEvent) {
        const customer = selectEvent.target.item;
        console.debug("Setting customer for work order", { workOrder: workOrder.id, customer });

        setLoading(true);
        tenantApi(`/tenants/work-orders/${workOrder.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
                customerId: customer.id
            })
        }).then(res => {
            showNotification(`Customer added to the order`, 'success');
            setSelectedCustomer(customer);
            onCustomerChange?.(customer);
        }).catch(err => {
            console.error(err);
            showNotification(
                `Failed to add customer. ${err.message || 'Please try again.'}`,
                'error'
            );
        }).finally(() => {
            setLoading(false);
        });
    }

    function handleClearCustomer() {
        // Call update work order API and clear the customer
        console.debug("Clearing customer for work order", workOrder.id);

        setLoading(true);
        tenantApi(`/tenants/work-orders/${workOrder.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
                customerId: null // clear the customer ID from DB
            })
        }).then(res => {
            showNotification(`Customer removed from the order`, 'warning');
            setSelectedCustomer(null);
            onCustomerChange?.(null);
        }).catch(err => {
            console.error(err);
            showNotification(
                `Failed to remove customer. ${err.message || 'Please try again.'}`,
                'error'
            );
        }).finally(() => {
            setLoading(false);
        });
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" display="flex" alignItems="center" fontWeight="bold">
                    <PersonIcon sx={{ mr: 1 }} />
                    Customer
                </Typography>
            </Box>

            {selectedCustomer ? (
                <Box>
                    <Box>
                        <Typography variant="subtitle1">{selectedCustomer.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            {selectedCustomer.phoneNumber}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {selectedCustomer.email}
                        </Typography>
                    </Box>
                    <Button
                        onClick={handleClearCustomer}
                        disabled={disabled || loading}
                        startIcon={<ClearIcon />}
                        size="small"
                        sx={{ mt: 1 }}
                    >
                        Clear Customer
                    </Button>
                </Box>
            ) : (
                <ItemPicker
                    component="Button"
                    label="Select Customer"
                    variant='outlined'
                    ListView={CustomersList}
                    addButton={true}
                    onChange={handleSelectCustomer}
                    fullWidth={false}
                    disabled={disabled || loading}
                />
            )}
            {loading && <LinearProgress />}
        </Box>
    );
}
